import type MpcClient from '@passkeys/mpc-client';
import {
  WindowTransport,
  RelayTransport,
  type RPC,
  type Transport,
} from '@passkeys/rpc';

import {
  ALLOWED_ORIGINS,
  PRIVATE_BROWSING_MESSAGE,
  RETRY_OPEN_MESSAGE,
} from '@/background/features/passkey/constants';
import { getAppId } from '@/utils/app-config';
import { isSafariPrivateWindow } from '@/utils/browser';
import { getTransportTarget, getTransportWindow } from './utils.js';

type RelayOptions = {
  enabled: boolean;
  returnTo?: string | null;
};

type Dependencies = {
  modalRpc: RPC;
  mpcClient: MpcClient;
  relay: RelayOptions;
};

const isPrivateBrowsing = async () => {
  const isSafariPrivate = await isSafariPrivateWindow();

  return (
    isSafariPrivate &&
    !(__SKIP_SAFARI_INCOGNITO_CHECK__ && window.SKIP_SAFARI_INCOGNITO_CHECK)
  );
};

const createTransport = async ({
  modalRpc,
  mpcClient,
  relay,
}: Dependencies): Promise<Transport | undefined> => {
  if (relay.enabled) {
    return new RelayTransport({
      mpcClient,
      config: {
        signerUrl: __PASSKEYS_NETWORK_URL__,
        queryParams: { returnTo: relay.returnTo, appId: getAppId() },
      },
      ...(__GLOBAL_SIGNER_METHODS__ && {
        openSigner: window.nativeBridge?.openSigner,
        closeSigner: window.nativeBridge?.closeSigner,
      }),
    });
  }

  const privateBrowsing = await isPrivateBrowsing();
  if (privateBrowsing) {
    await modalRpc.callMethod('notify', PRIVATE_BROWSING_MESSAGE);
    return;
  }

  const transportTarget = getTransportTarget();
  let transportWindow = await getTransportWindow();
  if (__STANDALONE_MODE__ && !transportWindow) {
    const retryOpen = await modalRpc.callMethod('confirm', RETRY_OPEN_MESSAGE);

    if (retryOpen) {
      transportWindow = await getTransportWindow();
    }
  }
  if (!transportWindow) {
    console.warn('passkey: unable to get window instance');
    return;
  }

  return new WindowTransport({
    window: transportWindow,
    target: transportTarget,
    allowedOrigins: ALLOWED_ORIGINS,
  });
};

export default createTransport;
